/**
 * Help Center - Floating help button with searchable guides
 * 
 * Features:
 * - Floating button (bottom-right corner)
 * - Searchable help articles
 * - Step-by-step guides with icons
 * - Category filtering
 * - Keyboard shortcut (? key)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { helpCenterAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  HelpCircle,
  Search,
  X,
  ChevronRight,
  ChevronLeft,
  Building2,
  DoorOpen,
  Tag,
  Link,
  Unlink,
  Settings,
  Star,
  Users,
  Shield,
  ClipboardCheck,
  BarChart3,
  DollarSign,
  Edit,
  Plus,
  Check,
  Copy,
  Eye,
  Play,
  ArrowUp,
  ArrowDown,
  ToggleRight,
  FileText,
  Smartphone,
  Loader2,
} from 'lucide-react';

// Icon mapping for step icons
const STEP_ICONS = {
  building: Building2,
  door: DoorOpen,
  tag: Tag,
  link: Link,
  unlink: Unlink,
  settings: Settings,
  star: Star,
  users: Users,
  shield: Shield,
  clipboard: ClipboardCheck,
  chart: BarChart3,
  dollar: DollarSign,
  edit: Edit,
  plus: Plus,
  check: Check,
  copy: Copy,
  eye: Eye,
  play: Play,
  form: FileText,
  click: ChevronRight,
  search: Search,
  arrows: ArrowUp,
  toggle: ToggleRight,
  smartphone: Smartphone,
  compare: BarChart3,
  filter: Search,
  tabs: FileText,
  key: Shield,
};

export default function HelpCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch articles on search/category change
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await helpCenterAPI.getArticles(
        searchQuery || null,
        selectedCategory || null
      );
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Failed to fetch help articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await helpCenterAPI.getCategories();
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    
    if (isOpen) {
      fetchCategories();
      fetchArticles();
    }
  }, [isOpen, fetchArticles]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setTimeout(() => {
      fetchArticles();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, isOpen, fetchArticles]);

  // Keyboard shortcut (? key)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '?' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const openArticle = (article) => {
    setSelectedArticle(article);
  };

  const backToList = () => {
    setSelectedArticle(null);
  };

  const getStepIcon = (iconName) => {
    const IconComponent = STEP_ICONS[iconName] || ChevronRight;
    return IconComponent;
  };

  return (
    <>
      {/* Floating Help Button */}
      <Button
        data-testid="help-center-button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
        title="Help Center (Press ? for keyboard shortcut)"
      >
        <HelpCircle className="w-6 h-6 text-white" />
      </Button>

      {/* Help Panel */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent 
          side="right" 
          className="w-full sm:w-[450px] bg-[hsl(217,19%,12%)] border-l border-[hsl(217,19%,27%)] p-0 overflow-hidden"
        >
          {selectedArticle ? (
            // Article Detail View
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-[hsl(217,19%,27%)]">
                <button
                  onClick={backToList}
                  className="flex items-center text-sm text-blue-400 hover:text-blue-300 mb-3"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to Help
                </button>
                <h2 className="text-xl font-semibold text-white">{selectedArticle.title}</h2>
                <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-400">
                  {selectedArticle.category}
                </span>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {selectedArticle.steps?.map((step, index) => {
                    const StepIcon = getStepIcon(step.icon);
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <StepIcon className="w-4 h-4 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs font-medium text-blue-400">Step {index + 1}</span>
                          </div>
                          <p className="text-sm text-[hsl(210,40%,96%)]">{step.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            // Article List View
            <div className="flex flex-col h-full">
              <SheetHeader className="p-4 border-b border-[hsl(217,19%,27%)]">
                <SheetTitle className="text-xl font-semibold text-white flex items-center">
                  <HelpCircle className="w-5 h-5 mr-2 text-blue-500" />
                  Help Center
                </SheetTitle>
                <SheetDescription className="text-[hsl(215,20%,65%)]">
                  Search for help or browse by category
                </SheetDescription>
              </SheetHeader>

              <div className="p-4 border-b border-[hsl(217,19%,27%)]">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,20%,65%)]" />
                  <Input
                    data-testid="help-search-input"
                    placeholder="Search help articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,50%)]"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,20%,65%)] hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Category Pills */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      selectedCategory === null
                        ? 'bg-blue-500 text-white'
                        : 'bg-[hsl(217,19%,20%)] text-[hsl(215,20%,65%)] hover:text-white'
                    }`}
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        selectedCategory === category
                          ? 'bg-blue-500 text-white'
                          : 'bg-[hsl(217,19%,20%)] text-[hsl(215,20%,65%)] hover:text-white'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Articles List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  </div>
                ) : articles.length === 0 ? (
                  <div className="text-center py-12 text-[hsl(215,20%,65%)]">
                    <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No articles found</p>
                    {searchQuery && (
                      <p className="text-sm mt-1">Try a different search term</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-[hsl(217,19%,20%)]">
                    {articles.map((article) => (
                      <button
                        key={article.id}
                        data-testid={`help-article-${article.id}`}
                        onClick={() => openArticle(article)}
                        className="w-full p-4 text-left hover:bg-[hsl(217,19%,17%)] transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-white mb-1">
                              {article.title}
                            </h3>
                            <span className="text-xs text-blue-400">{article.category}</span>
                            {article.keywords && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {article.keywords.slice(0, 3).map((keyword, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-0.5 text-xs rounded bg-[hsl(217,19%,20%)] text-[hsl(215,20%,65%)]"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-[hsl(215,20%,50%)] flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 border-t border-[hsl(217,19%,27%)] bg-[hsl(217,19%,10%)]">
                <p className="text-xs text-center text-[hsl(215,20%,50%)]">
                  Press <kbd className="px-1.5 py-0.5 text-xs rounded bg-[hsl(217,19%,20%)] text-white">?</kbd> to open Help Center
                </p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
